import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useCallback, useState } from "react";
import * as styles from "./ask-redo-confirmation.module.css";

/**
 * Merchant-facing confirmation card rendered when a tool is in
 * `approval-requested` (or briefly `approval-responded`) state.
 *
 * The copy is merchant-facing, NOT developer-facing. The caller
 * (`ask-redo-message.tsx`) is expected to translate the tool's
 * intent into plain English via `title` + `description` and to label
 * the buttons for the actual action ("Connect me with support" vs.
 * "Not yet"), not raw "approve / deny" jargon.
 *
 * Double-submit safety: a local `pending` flag latches on the first
 * click and disables both buttons for the remaining lifetime of the
 * card. The flag lives inside the component (rather than relying on
 * the parent to disable) because the parent renders inside a
 * streaming message list — re-renders on every text-delta — so
 * defending in place is simpler than asking the parent to memoize a
 * per-card disabled flag.
 */
export interface AskRedoConfirmationProps {
  title: string;
  description?: string;
  /** Extra context slot — rarely used. Set for power/internal flows
   *  that actually want a details block (e.g. developer preview). */
  details?: ReactNode;
  approveLabel: string;
  denyLabel: string;
  onApprove: () => void;
  onDeny: () => void;
}

export const AskRedoConfirmation = memo(function AskRedoConfirmation({
  title,
  description,
  details,
  approveLabel,
  denyLabel,
  onApprove,
  onDeny,
}: AskRedoConfirmationProps) {
  const [pending, setPending] = useState(false);

  const handleApprove = useCallback(() => {
    if (pending) {
      return;
    }
    setPending(true);
    onApprove();
  }, [pending, onApprove]);

  const handleDeny = useCallback(() => {
    if (pending) {
      return;
    }
    setPending(true);
    onDeny();
  }, [pending, onDeny]);

  return (
    <div className={styles.card}>
      <Flex dir="column" gap="sm">
        <Text fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
        {description && (
          <Text fontSize="sm" textColor="secondary">
            {description}
          </Text>
        )}
        {details && <div className={styles.details}>{details}</div>}
        <Flex gap="sm" justify="flex-end">
          <RedoButton
            disabled={pending}
            hierarchy="secondary"
            onClick={handleDeny}
            size="sm"
            text={denyLabel}
          />
          <RedoButton
            disabled={pending}
            hierarchy="primary"
            onClick={handleApprove}
            size="sm"
            text={approveLabel}
          />
        </Flex>
      </Flex>
    </div>
  );
});
